<script setup lang="ts">
import { inject, computed } from 'vue';
import { gettext, pgettext } from '@integro/gettext';
import type { TableModel } from './model';
import ItpDataTableRow from './ItpDataTableRow.vue';
import ItpDataTableFooterTotalsRow from './ItpDataTableFooterTotalsRow.vue';

const model = inject<TableModel>('model');
if (!model) {
  throw new Error('ItpDataTableBody missing model');
}

const noDataText = computed(() => gettext('No data available'));
const msgCreateRow = computed(
  () =>
    model.options.messages?.msgCreateRow || pgettext('ItpDataTable', 'Create'),
);

const opts = computed(() => model.options);

const totalColumnCount = computed(() => {
  const option = opts.value;
  return (
    option.columns.length +
    (option.rowSelection ? 1 : 0) +
    (option.rowHeader ? 1 : 0) +
    (option.fillEmptyRowSpace !== false ? 1 : 0) +
    (option.editable ? 1 : 0)
  );
});

const detailsLeadingOffset = computed(
  () => (opts.value.rowSelection ? 1 : 0) + (opts.value.rowHeader ? 1 : 0),
);

const detailsTrailingOffset = computed(
  () =>
    (opts.value.fillEmptyRowSpace !== false ? 1 : 0) +
    (opts.value.editable ? 1 : 0),
);

const detailsColspan = computed(
  () =>
    totalColumnCount.value -
    detailsLeadingOffset.value -
    detailsTrailingOffset.value,
);

const enableRowDetails = computed(
  () =>
    opts.value.rowDetailsMode === 'alwaysVisible' ||
    opts.value.rowDetailsMode === 'expandable',
);

const rowDetailsVisible = (item: any) =>
  item.isExpanded?.value || opts.value.rowDetailsMode === 'alwaysVisible';

const getRowKey = (item: any, index: number): string => {
  if (item.isRow) {
    return item.key;
  }
  if (item.isRowGroup) {
    return `group_${item.id}`;
  }
  if (item.isAddRow) {
    return 'add_row';
  }
  if (item.isAddRowToGroup) {
    return `add_row_${item.group.id}`;
  }
  if (item.isFooterTotalsRow) {
    return item.rowKey || 'footer_totals';
  }
  if (item.isSlotRow) {
    return `slot_${index}`;
  }
  return `item_${index}`;
};
</script>

<template>
  <tbody>
    <template
      v-for="(item, index) in model.view.value"
      :key="getRowKey(item, index)"
    >
      <tr
        v-if="item.isRowGroup"
        class="row-group"
        :class="{
          expanded: item.isExpanded,
          odd: index % 2 === 1,
          even: index % 2 === 0,
        }"
      >
        <td :colspan="totalColumnCount" class="row-group">
          <slot name="rowGroupHeader" :group="item" :rows="item.rows">
            {{ item.id }}
          </slot>
        </td>
      </tr>

      <tr
        v-else-if="item.isAddRow || item.isAddRowToGroup"
        class="itp-datatable__add-row"
      >
        <td :colspan="totalColumnCount" class="text-center border-0">
          <a
            href="#"
            class="itp-datatable__add-row-link"
            :class="{ disabled: model.isEditing }"
            :aria-disabled="model.isEditing ? 'true' : undefined"
            @click.prevent.stop="!model.isEditing && item.execute($event)"
          >
            <i class="fas fa-plus-square me-1" />
            {{ msgCreateRow }}
          </a>
        </td>
      </tr>

      <template v-else-if="item.isRow">
        <ItpDataTableRow
          :row="item"
          :columns="model.options.columns"
          :options="model.options"
          :class="{
            odd: index % 2 === 1,
            even: index % 2 === 0,
          }"
        />

        <tr
          v-if="enableRowDetails && rowDetailsVisible(item)"
          class="itp-datatable__row-details row-details"
          :class="{
            editing: item.isEditing?.value,
            expanded: item.isExpanded?.value,
            odd: index % 2 === 1,
            even: index % 2 === 0,
          }"
        >
          <td v-if="detailsLeadingOffset" :colspan="detailsLeadingOffset" />
          <td :colspan="detailsColspan" class="row-details">
            <slot name="rowDetails" :row="item" />
          </td>
          <td v-if="detailsTrailingOffset" :colspan="detailsTrailingOffset" />
        </tr>
      </template>

      <ItpDataTableFooterTotalsRow
        v-else-if="item.isFooterTotalsRow"
        :data="item"
      />

      <tr
        v-else-if="item.isSlotRow"
        class="itp-datatable__slotrow"
        :class="item.trClass"
      >
        <td :colspan="totalColumnCount" :class="item.tdClass">
          <hr v-if="item.ruler" class="my-0 text-muted opacity-25" />
          <slot :name="item.slotName" v-bind="item.bindings" />
        </td>
      </tr>
    </template>

    <tr v-if="model.view.value.length === 0" class="no-data-wrapper">
      <td :colspan="totalColumnCount" class="text-center py-5 text-muted">
        <div>
          {{ model.options.messages?.msgNoData || noDataText }}
        </div>
      </td>
    </tr>
  </tbody>
</template>
